import {buildAnchor, resolveAnchor} from './annotationAnchors';
import {HIGHLIGHT_NAME_BY_COLOR, UNDERLINE_HIGHLIGHT_NAME} from './annotationConstants';
import type {TextProjection} from './annotationTextProjection';
import {
  domRangeToNormalized,
  isNodeAnnotatable,
  normalizedRangeToDomRange,
  normalizedStartAt
} from './annotationTextProjection';
import type {Annotation, AnnotationAnchor, AnnotationRange, TextAnchor} from './annotationTypes';

function highlightNameForAnnotation(annotation: Annotation): string {
  return annotation.type === 'underline'
    ? UNDERLINE_HIGHLIGHT_NAME
    : HIGHLIGHT_NAME_BY_COLOR[annotation.color];
}

// Safari <17 exposes only WebKit's `caretRangeFromPoint`; other browsers the standard API.
interface CaretDocument {
  caretPositionFromPoint?: (x: number, y: number) => {offsetNode: Node; offset: number} | null;
  caretRangeFromPoint?: (x: number, y: number) => Range | null;
}

function caretFromPoint(x: number, y: number): {node: Node; offset: number} | null {
  const doc = document as unknown as CaretDocument;
  if (typeof doc.caretPositionFromPoint === 'function') {
    const position = doc.caretPositionFromPoint(x, y);
    return position ? {node: position.offsetNode, offset: position.offset} : null;
  }
  if (typeof doc.caretRangeFromPoint === 'function') {
    const range = doc.caretRangeFromPoint(x, y);
    return range ? {node: range.startContainer, offset: range.startOffset} : null;
  }
  return null;
}

/**
 * Painted highlights aren't elements, so hit-test via normalized offsets. Only
 * currently-resolving annotations can be hit (a dropped mark never opens a
 * popover); on overlap the most recently added one wins.
 */
export function findAnnotationAtPoint(
  container: Element,
  projection: TextProjection,
  x: number,
  y: number,
  annotations: Annotation[]
): AnnotationAnchor | null {
  const caret = caretFromPoint(x, y);
  if (!caret || !isNodeAnnotatable(caret.node, container)) {
    return null;
  }
  const offset = normalizedStartAt(projection, caret.node, caret.offset);
  for (let i = annotations.length - 1; i >= 0; i--) {
    const annotation = annotations[i];
    const resolved = resolveAnchor(annotation.anchor, projection);
    if (resolved === null || offset < resolved.start || offset >= resolved.end) {
      continue;
    }
    const range = normalizedRangeToDomRange(projection, resolved);
    if (range === null) {
      continue;
    }
    return {rect: range.getBoundingClientRect(), id: annotation.id};
  }
  return null;
}

/**
 * The Custom Highlight API is absent in jsdom and old browsers; without it
 * capture still works and only painting is skipped.
 */
export function isHighlightApiSupported(): boolean {
  return typeof Highlight !== 'undefined' && typeof CSS !== 'undefined' && Boolean(CSS.highlights);
}

/**
 * True when the container holds math MathJax will still rewrite — a
 * `.tex2jax_process` element without MathJax's `math/tex` output yet. MathJax
 * typesets ~250ms after mount and shifts text offsets, so callers defer the
 * first paint until it settles to avoid a flash against the stale DOM.
 */
export function hasUntypesetMath(container: Element): boolean {
  if (!window.MathJax?.Hub) {
    return false;
  }
  return Array.from(container.querySelectorAll('.tex2jax_process')).some(
    (element) => !element.querySelector('script[type^="math/tex"]')
  );
}

/**
 * Converts the current window selection to normalized offsets. Null when the
 * selection is missing, collapsed, or covers nothing annotatable.
 */
export function getSelectionNormalizedRange(projection: TextProjection): AnnotationRange | null {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
    return null;
  }
  return domRangeToNormalized(projection, selection.getRangeAt(0));
}

/**
 * Captures the current selection as an anchor plus its popover rect, clearing
 * the selection so the painted mark replaces it. Null when the selection is
 * missing, collapsed, outside the projection, or whitespace-only. Takes a
 * projection getter so a plain click never pays for a projection build.
 */
export function captureSelectionAnchor(
  getProjection: () => TextProjection | null,
  region: {regionId: string; lang: string | null}
): {anchor: TextAnchor; rect: DOMRect} | null {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
    return null;
  }
  const projection = getProjection();
  if (!projection) {
    return null;
  }
  const range = selection.getRangeAt(0);
  const normalized = domRangeToNormalized(projection, range);
  const anchor = normalized && buildAnchor(projection, normalized, region);
  if (!anchor) {
    return null;
  }
  const rect = range.getBoundingClientRect();
  selection.removeAllRanges();
  return {anchor, rect};
}

/**
 * Resolves each anchor (validate-or-drop) and buckets the Ranges by highlight
 * name; dropped anchors are skipped, never approximated. Painting the result
 * is the coordinator's job (annotationPaintCoordinator.ts).
 */
export function resolveAnnotationRanges(
  projection: TextProjection,
  annotations: Annotation[]
): Map<string, Range[]> {
  const rangesByName = new Map<string, Range[]>();
  annotations.forEach((annotation) => {
    const resolved = resolveAnchor(annotation.anchor, projection);
    const range = resolved && normalizedRangeToDomRange(projection, resolved);
    if (!range) {
      return;
    }
    const name = highlightNameForAnnotation(annotation);
    const ranges = rangesByName.get(name) ?? [];
    ranges.push(range);
    rangesByName.set(name, ranges);
  });
  return rangesByName;
}

/**
 * Watches `target`'s subtree. `onMutate` fires immediately on each mutation
 * batch; `onSettle` fires once mutations stop (trailing debounce). Highlight
 * Ranges are live, so a painted highlight slides as MathJax rewrites the prompt —
 * callers clear via `onMutate` and repaint the settled DOM via `onSettle`.
 */
export const SETTLE_DEBOUNCE_MS = 150;

export function observeSubtreeChanges(
  target: Element,
  handlers: {onMutate?: () => void; onSettle: () => void},
  options: {debounceMs?: number; attributeFilter?: string[]} = {}
): () => void {
  const {onMutate, onSettle} = handlers;
  const {debounceMs = SETTLE_DEBOUNCE_MS, attributeFilter} = options;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const observer = new MutationObserver(() => {
    onMutate?.();
    clearTimeout(timer);
    timer = setTimeout(onSettle, debounceMs);
  });
  observer.observe(target, {
    childList: true,
    subtree: true,
    characterData: true,
    ...(attributeFilter ? {attributes: true, attributeFilter} : {})
  });
  return () => {
    clearTimeout(timer);
    observer.disconnect();
  };
}
